import { canEditNote as canEditNoteService } from '@/services/permissions';
import {
  LABS_WRITE_ROLES,
  MEDICATION_WRITE_ROLES,
  PARTNER_TIMELINE_ROLES,
  ProviderRole,
  RESTRICTED_CONFIDENTIALITY_LEVEL_ROLES,
  SCHEDULER_MANAGER_ROLES,
} from '@bariendo/lib/constants';
import {
  canAccessPayments as canAccessPaymentsRule,
  canViewConversations as canViewConversationsRule,
  canRecordBenefitEvents as canRecordBenefitEventsRule,
  canEditInsuranceCard as canEditInsuranceCardRule,
  canViewPayments as canViewPaymentsRule,
  canViewIntakeHub as canViewIntakeHubRule,
  canViewCoverageHub as canViewCoverageHubRule,
  canViewFinanceOps as canViewFinanceOpsRule,
  canViewClinicalRecord as canViewClinicalRecordRule,
  canConfigureIvr as canConfigureIvrRule,
  canCreatePatientAccessNote as canCreatePatientAccessNoteRule,
  canEditOnCallSchedule as canEditOnCallScheduleRule,
  canViewCiManagerDashboard as canViewCiManagerDashboardRule,
  getAuthMeInfo,
  getPrimaryProviderRole,
  isAdmin as isAdminRole,
  checkIsPhysicianOnly,
  isProvider,
} from '@bariendo/lib/profile';
import { getReferenceString } from '@medplum/core';
import { Communication, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { CurrentUserContext } from './contexts';
import { ProviderDirectoryProvider } from './ProviderDirectoryProvider';

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
  const medplum = useMedplum();
  const currentUser = useMedplumProfile() as Practitioner;
  const posthog = usePostHog();

  const [currentUserRole, setCurrentUserRole] = useState<ProviderRole | undefined>();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [hasAdminMembership, setHasAdminMembership] = useState<boolean>(false);
  const [canAccessRestrictedContent, setCanAccessRestrictedContent] = useState<boolean>(false);
  const [canWriteMedications, setCanWriteMedications] = useState<boolean>(false);
  const [canWriteLabs, setCanWriteLabs] = useState<boolean>(false);
  const [canAccessPartners, setCanAccessPartners] = useState<boolean>(false);
  const [canManageSchedules, setCanManageSchedules] = useState<boolean>(false);
  const [canConfigureIvr, setCanConfigureIvr] = useState<boolean>(false);
  const [canEditOnCallSchedule, setCanEditOnCallSchedule] = useState<boolean>(false);
  const [canAccessPayments, setCanAccessPayments] = useState<boolean>(false);
  const [canViewPayments, setCanViewPayments] = useState<boolean>(false);
  const [canViewIntakeHub, setCanViewIntakeHub] = useState<boolean>(false);
  const [canViewCoverageHub, setCanViewCoverageHub] = useState<boolean>(false);
  const [canViewFinanceOps, setCanViewFinanceOps] = useState<boolean>(false);
  const [canViewCiManagerDashboard, setCanViewCiManagerDashboard] = useState<boolean>(false);
  const [canCreatePatientAccessNote, setCanCreatePatientAccessNote] = useState<boolean>(false);
  const [canViewClinicalRecord, setCanViewClinicalRecord] = useState<boolean>(false);
  const [canViewConversations, setCanViewConversations] = useState<boolean>(false);
  const [canRecordBenefitEvents, setCanRecordBenefitEvents] = useState<boolean>(false);
  const [canEditInsuranceCard, setCanEditInsuranceCard] = useState<boolean>(false);
  const [permissionsLoading, setPermissionsLoading] = useState<boolean>(true);
  const [isPhysicianOnly, setIsPhysicianOnly] = useState<boolean>(false);

  useEffect(() => {
    async function fetchPermissions() {
      setPermissionsLoading(true);
      try {
        if (!isProvider(currentUser)) {
          throw new Error('Current user is not a provider');
        }

        // Setup Sentry user
        Sentry.setUser({
          id: getReferenceString(currentUser),
        });

        const { membership, user } = await getAuthMeInfo(medplum);

        // Setup Posthog user
        posthog.identify(getReferenceString(currentUser), {
          email: user.email,
        });

        setHasAdminMembership(membership?.admin === true);

        const practitionerRole = await medplum.searchOne('PractitionerRole', {
          practitioner: getReferenceString(currentUser),
        });

        setIsAdmin(isAdminRole(practitionerRole));

        const role = getPrimaryProviderRole(practitionerRole);
        setCurrentUserRole(role);

        setCanAccessRestrictedContent(
          (role && RESTRICTED_CONFIDENTIALITY_LEVEL_ROLES.includes(role)) ||
            isAdminRole(practitionerRole) ||
            membership?.admin ||
            false
        );
        setCanWriteMedications(
          (role && MEDICATION_WRITE_ROLES.includes(role)) || isAdminRole(practitionerRole) || membership?.admin || false
        );
        setCanWriteLabs(
          (role && LABS_WRITE_ROLES.includes(role)) || isAdminRole(practitionerRole) || membership?.admin || false
        );
        setCanAccessPartners(
          (role && PARTNER_TIMELINE_ROLES.includes(role)) || isAdminRole(practitionerRole) || membership?.admin || false
        );
        setCanManageSchedules(
          (role && SCHEDULER_MANAGER_ROLES.includes(role)) ||
            isAdminRole(practitionerRole) ||
            membership?.admin ||
            false
        );
        // Deliberately not `|| membership?.admin`: ProjectMembership.admin grants
        // project administration, not the AccessPolicy that permits these writes.
        setCanConfigureIvr(canConfigureIvrRule(practitionerRole));
        setCanEditOnCallSchedule(canEditOnCallScheduleRule(practitionerRole));
        setCanAccessPayments(canAccessPaymentsRule(practitionerRole));
        setCanViewPayments(canViewPaymentsRule(practitionerRole));
        setCanViewIntakeHub(canViewIntakeHubRule(practitionerRole));
        setCanViewCoverageHub(canViewCoverageHubRule(practitionerRole));
        setCanViewFinanceOps(canViewFinanceOpsRule(practitionerRole));
        setCanViewCiManagerDashboard(canViewCiManagerDashboardRule(practitionerRole));
        setCanCreatePatientAccessNote(canCreatePatientAccessNoteRule(practitionerRole));
        setCanViewClinicalRecord(canViewClinicalRecordRule(practitionerRole));
        setCanViewConversations(canViewConversationsRule(practitionerRole));
        setCanRecordBenefitEvents(canRecordBenefitEventsRule(practitionerRole));
        setCanEditInsuranceCard(canEditInsuranceCardRule(practitionerRole));
        setIsPhysicianOnly(checkIsPhysicianOnly(practitionerRole));
      } catch (_error) {
        setCurrentUserRole(undefined);
        setHasAdminMembership(false);
        setCanAccessRestrictedContent(false);
        setCanWriteMedications(false);
        setCanWriteLabs(false);
        setCanAccessPartners(false);
        setCanConfigureIvr(false);
        // Fails closed: a permission fetch that threw must not leave payments
        // reachable.
        setCanAccessPayments(false);
        setCanViewPayments(false);
        setCanViewIntakeHub(false);
        setCanViewCoverageHub(false);
        setCanViewFinanceOps(false);
        // Likewise — the manager dashboard reports on the whole PAC team, so a
        // failed permission fetch must not leave it open.
        setCanViewCiManagerDashboard(false);
        setCanCreatePatientAccessNote(false);
        setCanViewClinicalRecord(false);
        setCanViewConversations(false);
        setCanRecordBenefitEvents(false);
        setCanEditInsuranceCard(false);
        setIsPhysicianOnly(false);
      } finally {
        setPermissionsLoading(false);
      }
    }

    fetchPermissions();
  }, [medplum, currentUser, posthog]);

  const canEditNote = (note: Communication) => canEditNoteService(note, currentUser, currentUserRole);

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser,
        currentUserRole,
        permissionsLoading,
        currentUserPermissions: {
          isAdmin,
          isPhysicianOnly,
          hasAdminMembership,
          canAccessRestrictedContent,
          canWriteMedications,
          canWriteLabs,
          canAccessPartners,
          canManageSchedules,
          canConfigureIvr,
          canEditOnCallSchedule,
          canAccessPayments,
          canViewPayments,
          canViewIntakeHub,
          canViewCoverageHub,
          canViewFinanceOps,
          canViewCiManagerDashboard,
          canCreatePatientAccessNote,
          canViewClinicalRecord,
          canViewConversations,
          canRecordBenefitEvents,
          canEditInsuranceCard,
          canEditNote,
        },
      }}
    >
      <ProviderDirectoryProvider>{children}</ProviderDirectoryProvider>
    </CurrentUserContext.Provider>
  );
}
